import React, { useContext, useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { OrderDataProviderContext } from '../../context-api/data-provider/OrderDataProvider';
import { API_NETWORK_ERROR, localStorageKeys } from '../../utils/constants';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';
import ActionOrders from './ActionOrders';
import InProgressOrder from './InProgressOrder';
import ReadyOrder from './ReadyOrder';
import ScheduledOrder from './ScheduledOrder';
import NewOrderAlert from '../../composed/neworderalert/NewOrderAlert';
import AtomicDialog from '../../atomic/dialog/Dialog';
import AllOrders from './AllOrders';
import moment from 'moment-timezone';
import { Error404 } from '../../composed/error404/Error404';
import RestaurantDelivery from './RestaurantDelivery';
import './order-dashboard-layout.scss';
import ReferralStatistics from '../../composed/referralStatistics/ReferralStatistics';
import { SignUpDataContext } from '../../context-api/data-provider/SignupDataProvider';
import { useNavigate } from 'react-router-dom';

const formattedDateTime = () => {
  return moment().format('MM/DD/YYYY - hh:mm A');
};

const OrderMainDashboard = () => {
  const { TOKEN } = localStorageKeys;
  const navigate = useNavigate();
  const { getOrderAPIResponse, FetchOrders, checkOrderTime, KitchenName, currentDate } =
    useContext(OrderDataProviderContext);

  const { onLoadInfoResponse } = useContext(GlobalStateDataContext);
  const [orderDataFromSocket, setOrderDataFromSocket] = useState(null);

  const { selectedMenuTab, setOpenOrderAlert, newPendingOrder, allOrders, openOrderAlert } =
    useContext(GlobalStateDataContext);

  const { success, message, data, loading } = getOrderAPIResponse;

  // console.log('----newPendingOrder', newPendingOrder)
  // console.log('---- preparingOrder', preparingOrder)
  // console.log('-----setScheduledOrder', scheduledOrder)
  // console.log('---- orderReadyForPickup', orderReadyForPickup)
  // console.log('---allOrders', allOrders)
  // console.log('---getOrderAPIResponse', getOrderAPIResponse)

  useEffect(() => {
    if (orderDataFromSocket) {
      checkOrderTime(orderDataFromSocket);
    }
  }, [JSON.stringify(orderDataFromSocket)]);

  const socketRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const dbFetchedOrderNoRef = useRef(new Set());
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [notificationServerDown, setNotificationServerDown] = useState(false);
  const URL_PATH_NOTIFICATION_SERVER = process.env.REACT_APP_NOTIFICATION_SERVER_URL; // WE HAVE TO REPLACE BEFORE GOING GIVE

  const { onLogout } = useContext(SignUpDataContext);
  const RedirectToLogin = async () => navigate('/login');

  const triggerLogout = async () => {
    await onLogout();
    await RedirectToLogin();
    window.location.reload();
  };
  useEffect(() => {
    const RELOAD_LOCK_KEY = 'kitchen_socket_reloaded_once';
    let reloadTimer = null;

    const socket = io(URL_PATH_NOTIFICATION_SERVER, {
      reconnection: true,
      reconnectionAttempts: Infinity,
      forceNew: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 2000,
      timeout: 5000,
      query: {
        type: 'kitchen_frontend_app',
        // kitchenName: KitchenName,
      },
      transports: ['websocket'],
      auth: {
        token: localStorage.getItem(TOKEN),
      },
    });

    socketRef.current = socket;

    const reloadOnceAfterDisconnect = () => {
      const alreadyReloaded = sessionStorage.getItem(RELOAD_LOCK_KEY);

      if (alreadyReloaded) return;

      sessionStorage.setItem(RELOAD_LOCK_KEY, 'true');
      reloadTimer = setTimeout(() => {
        window.location.reload();
      }, 1000);
    };

    socket.on('connect', () => {
      reconnectAttemptsRef.current = 0;
      setConnectionStatus('Connected');
      setNotificationServerDown(false);

      // Clear DB fallback processed orders
      dbFetchedOrderNoRef.current.clear();
      console.log(`Connected ${socket.id} ${formattedDateTime()}`);
    });

    socket.on('disconnect', (reason) => {
      setConnectionStatus('Disconnected: ' + reason);
      setNotificationServerDown(true);
      setOpenOrderAlert(true);

      console.log(`Disconnected ${reason} ${formattedDateTime()}`);

      // ✅ Reload only after disconnect
      reloadOnceAfterDisconnect();
    });

    socket.on('reconnect_attempt', (attemptNumber) => {
      reconnectAttemptsRef.current = attemptNumber;
      setConnectionStatus(`Reconnecting... Attempt ${attemptNumber}`);
      console.log(`Reconnection attempt #${attemptNumber}`);
    });

    socket.on('reconnect_failed', () => {
      setConnectionStatus('Reconnection failed');
      setNotificationServerDown(true);
      console.error('Notification server failed to reconnect');
    });

    socket.on('reconnect', () => {
      setNotificationServerDown(false);
      setConnectionStatus('Reconnected successfully');
      console.log('Notification server reconnected successfully');
    });

    socket.on('connect_error', (e) => {
      console.log(`Socket Server is down ${e.message} ${formattedDateTime()}`);
      setNotificationServerDown(true);
    });

    socket.on('socket-auth-error', (data) => {
      console.log('Socket auth error:', data);

      if (data?.message === 'Invalid token') {
        console.log('Logout user because token is invalid');
        triggerLogout();
        setNotificationServerDown(true);
      }
    });

    const handleKitchenSocketData = async (arg, callback) => {
      try {
        // console.log('Data coming from Notification server:', arg);

        if (arg && arg.internal_order_status === 'pending' && arg?.delivery_date === currentDate) {
          setOpenOrderAlert(true);
        }

        if (
          arg &&
          (arg.internal_order_status === 'driver pickup pending' ||
            arg.internal_order_status === 'driver picked the order' ||
            arg.internal_order_status === 'order delivered') &&
          arg.joined_order_no !== null
        ) {
          FetchOrders();
        } else if (arg?.delivery_time_slot) {
          setOrderDataFromSocket(arg);
        }

        if (typeof callback === 'function') {
          callback({
            received: true,
            kitchenName: KitchenName,
            order_no: arg?.order_no || null,
            joined_order_no: arg?.joined_order_no || null,
            receivedAt: new Date().toISOString(),
            message: 'Kitchen frontend received data successfully',
          });
        }
      } catch (error) {
        console.error('Error processing socket data:', error);

        if (typeof callback === 'function') {
          callback({
            received: false,
            kitchenName: KitchenName,
            error: error.message,
            receivedAt: new Date().toISOString(),
            message: 'Kitchen frontend failed to process data',
          });
        }
      }
    };

    const pingInterval = setInterval(() => {
      if (socket.connected) {
        socket.emit('ping-from-kitchen');
        console.log(`📡 Sent ping ${formattedDateTime()}`);
      }
    }, 60000);

    socket.on(`pong-${KitchenName}`, handleKitchenSocketData);
    socket.on(KitchenName, handleKitchenSocketData);

    return () => {
      if (reloadTimer) clearTimeout(reloadTimer);
      clearInterval(pingInterval);
      socket.off(`pong-${KitchenName}`, handleKitchenSocketData);
      socket.off(KitchenName, handleKitchenSocketData);
      socket.disconnect();
    };
  }, [KitchenName, currentDate]);

  useEffect(() => {
    let Interval = null;

    const fetchData = async () => {
      try {
        const orderData = await FetchOrders({ date: currentDate });
        const StoreType = onLoadInfoResponse?.data?.data?.data?.KitchenInfo?.account_type;
        if (notificationServerDown) {
          if (StoreType === 'restaurant') {
            if (orderData?.payAsYouGoOrders?.length > 0) {
              const pendingDbOrders = orderData.payAsYouGoOrders.filter(
                (order) => order?.internal_order_status === 'pending'
              );

              const newOrdersFromDb = pendingDbOrders.filter((order) => {
                const orderNo = order?.order_no;

                if (!orderNo) return false;

                if (dbFetchedOrderNoRef.current.has(orderNo)) {
                  return false;
                }

                return true;
              });

              // console.log('---newOrdersFromDb', newOrdersFromDb);
              // console.log('---already processed orders', Array.from(dbFetchedOrderNoRef.current));

              if (newOrdersFromDb.length > 0) {
                setOpenOrderAlert(true);
              }

              newOrdersFromDb.forEach((order) => {
                dbFetchedOrderNoRef.current.add(order.order_no);
                checkOrderTime(order);
              });
            }
          }

          if (StoreType === 'promotional kitchen') {
            if (orderData?.PromotionalOrders?.length > 0) {
              const pendingDbOrders = orderData.PromotionalOrders.filter(
                (order) => order?.internal_order_status === 'pending'
              );

              const newOrdersFromDb = pendingDbOrders.filter((order) => {
                const orderNo = order?.order_no;

                if (!orderNo) return false;

                if (dbFetchedOrderNoRef.current.has(orderNo)) {
                  return false;
                }

                return true;
              });

              // console.log('---newOrdersFromDb', newOrdersFromDb);
              // console.log('---already processed orders', Array.from(dbFetchedOrderNoRef.current));

              if (newOrdersFromDb.length > 0) {
                setOpenOrderAlert(true);
              }

              newOrdersFromDb.forEach((order) => {
                dbFetchedOrderNoRef.current.add(order.order_no);
                checkOrderTime(order);
              });
            }
          }

          /* Later we have to add for other account_types like    
          enum: ['cloud kitchen','restaurant','home chef','catering','celebrity chef','promotional kitchen','grocery store','wholesale store', ] */
        }

        console.log('Fetching order on setInterval');
      } catch (error) {
        console.log('FetchOrders error', error);
      }
    };

    fetchData();

    if (notificationServerDown) {
      Interval = setInterval(() => {
        fetchData();
      }, 300000);
    }

    return () => {
      if (Interval) {
        clearInterval(Interval);
      }
    };
  }, [notificationServerDown, currentDate]);

  if (success === false) {
    if (message === 'Request timed out') {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            fontFamily: 'Inter Medium',
            fontSize: '20px',
            color: 'red',
            marginTop: '20px',
          }}
        >
          {' '}
          The request took too long. Please try again.
        </div>
      );
    } else if (message === API_NETWORK_ERROR) {
      return <Error404 />;
    } else if (message === 'Authentication failed!') {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            fontFamily: 'Inter Medium',
            fontSize: '20px',
            color: 'red',
            marginTop: '20px',
          }}
        >
          {' '}
          Session expired — please log in again to continue.
        </div>
      );
    } else {
      console.log(
        '----We are working on fixing an unexpected issue. Please check back shortly',
        message
      );
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            fontFamily: 'Inter Medium',
            fontSize: '20px',
            color: 'red',
            marginTop: '20px',
          }}
        >
          {' '}
          We're working on fixing an unexpected issue. Please check back shortly.
        </div>
      );
    }
  }

  return (
    <div
      className={
        selectedMenuTab === 'restaurantdelivery' ? 'order-dashboard order-dashboard--map-tab' : 'order-dashboard'
      }
    >
      {newPendingOrder?.length <= 0 && selectedMenuTab === 'action' && (
        <ReferralStatistics redirect={true} />
      )}
      {selectedMenuTab === 'action' && <ActionOrders />}
      {selectedMenuTab === 'inprogress' && <InProgressOrder />}
      {selectedMenuTab === 'orderReady' && <ReadyOrder />}
      {selectedMenuTab === 'allorders' && <AllOrders />}
      {selectedMenuTab === 'restaurantdelivery' && <RestaurantDelivery />}
      <ScheduledOrder />

      <AtomicDialog>
        <NewOrderAlert />
      </AtomicDialog>
    </div>
  );
};

export default OrderMainDashboard;
